import * as msal from '@azure/msal-browser';
import { PublicClientApplication } from '@azure/msal-browser';
import getAzureAuthConfig from './AzureAuthConfig';
import callMSGraph from './AzureGraphHelper';
import graphConfig from './AzureGraphConfig';

let initialized = false;
let myMSALObj;
let msalConfig;
let loginRequest;
let tokenRequest;
let username;

/**
 * @typedef AzureAuthProviderConfig
 * @type {object}
 * @property {string} clientId - the azure clientId
 * @property {string} authority - the azure authority
 */

/**
 * @typedef TokenResponse
 * @type {object}
 * @property {object} account - the account information
 * @property {string} idToken - the id token returned by azure
 * @property {string} authority - the authority
 */
/**
 * Initializes the Azure instance
 *
 * @param @type {AzureAuthProviderConfig} azure auth provider configuration
 */
function init(azureAuthProviderConfig) {
  if (!initialized) {
    const { clientId, authority } = azureAuthProviderConfig;
    console.log('AzureAuthRedirect init with clientId, authority', clientId, authority);
    let azureAuthConfig = getAzureAuthConfig(clientId, authority);

    msalConfig = azureAuthConfig.msalConfig;
    tokenRequest = azureAuthConfig.tokenRequest;
    loginRequest = azureAuthConfig.loginRequest;
    username = '';

    // Create the main myMSALObj instance
    myMSALObj = new PublicClientApplication(msalConfig);

    initialized = true;
  } else {
    console.log('AzureAuthRedirect was initialized before. Skipped');
  }
}

/**
 * Initiate login, be it a real sign in or if not necessary using the stored azure login.
 */
async function initiateLogin() {
  /**
   * A promise handler needs to be registered for handling the
   * response returned from redirect flow. For more information, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/acquire-token.md
   */

  let response = await myMSALObj.handleRedirectPromise();
  return await handleResponse(response);
}

function updateUI(data, endpoint) {
  console.log('Graph API responded at: ' + new Date().toString());
}

/**
 * Login response handler
 * @param {*} response
 * @returns @type {TokenResponse}
 */
async function handleResponse(response) {
  let account;
  if (response !== null) {
    console.log('handleResponse - this is the response from a real login');

    // this is the response from a real login
    account = response.account;
    if (account) {
      username = response.account.username;
      myMSALObj.setActiveAccount(account);
    }
  } else {
    console.log('handleResponse but response is null, account has to be selected');

    account = selectAccount();
  }

  if (account) {
    return await getToken();
  } else {
    //signIn();
  }
  return null;
}

/**
 * See here for more info on account retrieval:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
 */
function selectAccount() {
  const currentAccounts = myMSALObj.getAllAccounts();

  if (currentAccounts.length === 0) {
    console.log('selectAccount, yet no account found. Proceed with signIn');
    throw 'No Account found. Try signIn';
  } else if (currentAccounts.length > 1) {
    // Add your account choosing logic here
    console.warn('Multiple accounts detected.');
    console.log('selectAccount, yet multiple accounts found. What should we do?');
    throw new Error('Multiple accounts found. Selection is not implemented');
  } else {
    let account = currentAccounts[0];
    myMSALObj.setActiveAccount(account);

    username = currentAccounts[0].username;
    console.log('selectAccount, found one account', account);

    return account;
  }
}

async function getToken() {
  console.log('getToken');
  try {
    return await myMSALObj.acquireTokenSilent({});
  } catch (error) {
    console.error('Failed to acquire token via MSAL - proceed with logout', error);
  }
  await myMSALObj
    .acquireTokenSilent({})
    .then((tokenResponse) => {
      console.log('token response', tokenResponse.account.username, tokenResponse.idToken);
    })
    .catch((error) => {
      console.error('Failed to acquire token via MSAL - proceed with logout', error);
      // //TODO: invalidate cookie token
      // store.dispatch('lifecycle/setAppStates', { authenticated: false, msg: 'Login was unsuccessful' });
      // signOut();
    });
}

function signOut(cloudAuthProviderConfig) {
  this.init(cloudAuthProviderConfig);
  /**
   * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
   */
  let account = myMSALObj.getAccountByUsername(username);
  console.log('signout', username, account);
  const logoutRequest = {
    account,
    postLogoutRedirectUri: msalConfig.auth.redirectUri,
    onRedirectNavigate: (url) => {
      // Return false if you would like to stop navigation after local logout
      return false;
    },
  };
  myMSALObj.logoutRedirect(logoutRequest);
}

function getTokenRedirect(request) {
  /**
   * See here for more info on account retrieval:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   */
  request.account = myMSALObj.getAccountByUsername(username);

  return myMSALObj.acquireTokenSilent(request).catch((error) => {
    console.warn('silent token acquisition fails. acquiring token using redirect');
    if (error instanceof msal.InteractionRequiredAuthError) {
      // fallback to interaction when silent call fails
      return myMSALObj.acquireTokenRedirect(request);
    } else {
      console.warn(error);
    }
  });
}

function seeProfile() {
  this.getTokenRedirect(loginRequest)
    .then((response) => {
      callMSGraph(graphConfig.graphMeEndpoint, response.accessToken, updateUI);
    })
    .catch((error) => {
      console.error(error);
    });
}

function readMail() {
  this.getTokenRedirect(tokenRequest)
    .then((response) => {
      callMSGraph(graphConfig.graphMailEndpoint, response.accessToken, updateUI);
    })
    .catch((error) => {
      console.error(error);
    });
}

/**
 * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
 */
function signIn() {
  myMSALObj.loginRedirect(loginRequest);
}

export default {
  init,
  initiateLogin,
  signIn,
  signOut,
  readMail,
  getToken,
};
